import { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { HStack, Text } from '@library';
import usePriceFormatter from '../../../core/hooks/usePriceFormatter';
import { ProgressSpinner } from '../../../core/components/ProgressIndicators';
import type {
    LoggedInProductCustomerPrice,
    ProductWithAdditionalCharges,
} from '../../types';
import getAreAdditionalChargesApplicableForProduct from '../../util/getAreAdditionalChargesApplicableForProduct';
import Price from '../../../pricing/components/Price';
import type { PreorderPriceEditableProduct } from '../../../pricing/type';
import styles from './ProductPricing.module.css';
import ProductPriceDiscountTag from './ProductPriceDiscountTag';

const ProductMyHEKPrice = ({
    pricingInfo,
    loading = false,
    isStartingPrice = false,
    onSale = false,
    hideDiscountTags = false,
    topAlignPrices = false,
    productWithAdditionalCharges,
    product,
}: {
    pricingInfo?: LoggedInProductCustomerPrice;
    loading?: boolean;
    isStartingPrice?: boolean;
    onSale?: boolean;
    hideDiscountTags?: boolean;
    topAlignPrices?: boolean;
    productWithAdditionalCharges: ProductWithAdditionalCharges;
    product?: PreorderPriceEditableProduct;
}) => {
    const priceFormatter = usePriceFormatter();
    const formattedPrice = priceFormatter(pricingInfo?.finalPrice ?? 0);
    const formattedGeneralPrice = priceFormatter(
        pricingInfo?.generalDealerPrice ?? 0
    );

    const additionalCharges = getAreAdditionalChargesApplicableForProduct(
        productWithAdditionalCharges
    );

    return (
        <div data-test-class="priceContainer" data-test-state="customerPrice">
            {!hideDiscountTags && (
                <div className={styles.discountSection}>
                    {pricingInfo && (
                        <HStack justify="end" align="center">
                            <ProductPriceDiscountTag
                                customerPrice={pricingInfo}
                                onSale={onSale}
                            />
                            {pricingInfo.dealerDiscountPercentage > 0 ? (
                                <div className={styles.netDealerPrice}>
                                    <Text secondary bold strikeThrough>
                                        {formattedGeneralPrice.price}
                                    </Text>
                                </div>
                            ) : null}
                        </HStack>
                    )}
                </div>
            )}
            <HStack
                align={topAlignPrices ? 'baseline' : 'center'}
                justify="spaceBetween"
            >
                <HStack align="baseline">
                    <div className={styles.priceTitle}>
                        <Text tertiary bold>
                            <FormattedMessage id="my dealer price" />
                        </Text>
                    </div>
                    <div className={styles.taxInfo}>
                        <Text caption>
                            <FormattedMessage id="plus VAT" />
                            {additionalCharges && (
                                <>
                                    <span> </span>
                                    <FormattedMessage id="and additional charges" />
                                </>
                            )}
                        </Text>
                    </div>
                </HStack>
                {loading ? (
                    <div className={styles.spinner}>
                        <ProgressSpinner />
                    </div>
                ) : (
                    <Price
                        product={product}
                        customerPrice={pricingInfo}
                        retainPosition
                    >
                        <HStack align="baseline">
                            {isStartingPrice && (
                                <div className={styles.priceFromLabel}>
                                    <Text tertiary>
                                        <FormattedMessage id="from" />
                                    </Text>
                                </div>
                            )}
                            <div className={styles.currency}>
                                <Text tertiary>
                                    <FormattedMessage
                                        id={formattedPrice.currency}
                                    />
                                </Text>
                            </div>
                            <div className={styles.price}>
                                <Text primary bold>
                                    {formattedPrice.price}
                                </Text>
                            </div>
                        </HStack>
                    </Price>
                )}
            </HStack>
        </div>
    );
};

export default memo(ProductMyHEKPrice);
